// Import the tinycolor2 library for working with colours
import * as tinycolor from 'tinycolor2'

// Function to create a filter object for a vuex filter and return it as a gql filter object
function makeGqlFilter (vuexFilter) {
  // If the filter type is price, create a filter object with the field 'Price' and the values of the vuex filter
  if (vuexFilter.type === 'price') {
    return {
      field: 'Price',
      between: vuexFilter.values
    }
  }
  // If the filter type is colour, create a filter object with the field 'Option' and the subfield 'colour'
  // Also convert the vuex filter values to JSON strings
  else if (vuexFilter.type === 'colour') {
    return {
      field: 'Option',
      subfield: 'colour',
      // inRegex: vuexFilter.values.map(v => JSON.stringify(v))
      colourTerms: vuexFilter.values
    }
  }
  // Otherwise, create a filter object with the field 'Attribute' and the subfield of the vuex filter type
  else {
    return {
      field: 'Attribute',
      subfield: vuexFilter.type,
      in: vuexFilter.values
    }
  }
}

// Function to create a gql filter object from a search term
export function makeSearchFilter (searchTerm) {
  let filter = { and: [] }

  // Split the search term into individual words and create a filter object for each word
  for (const word of (searchTerm || '').split(' ')) {
    filter.and.push(makeSearchFilterForSingleTerm(word))
  }

  return filter
}

// Function to create a gql filter object for a single search term
function makeSearchFilterForSingleTerm (searchTerm) {
  let filter = { or: [] }

  // Create a filter object for the search term in the name field
  filter.or.push({
    field: 'Name',
    contains: searchTerm
  })

  // Create a filter object for the search term in the reference field
  filter.or.push({
    field: 'Reference',
    contains: searchTerm
  })

  // Create a filter object for the search term in the brand attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'brand',
    contains: searchTerm
  })

  // Create a filter object for the search term in the range attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'range',
    contains: searchTerm
  })

  // Create a filter object for the search term in the size attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'size',
    contains: searchTerm
  })

  // Create a filter object for the search term in the grip attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'grip',
    contains: searchTerm
  })

  // Create a filter object for the search term in the material attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'material',
    contains: searchTerm
  })

  // Create a filter object for the search term in the manufacturer attribute field
  filter.or.push({
    field: 'Attribute',
    subfield: 'manufacturer',
    contains: searchTerm
  })

  return filter
}

// Function to create a gql filter object from the vuex state filters and search term
export const productFilterGql = (state) => {
  // If there are no filters or search term, return an empty object
  if (!state.filters.length && !state.search) return {}

  let rootGqlFilter = {}

  // Create a filter object for each vuex filter and add it to the root filter object
  state.filters.forEach(filter => {
    let options = ['type', 'material', 'range', 'category']

    // if (options.includes(filter.type)) {
    //   if (!rootGqlFilter?.or) rootGqlFilter.or = []
    //   rootGqlFilter.or.push(makeGqlFilter(filter))
    // } else {
      if (!rootGqlFilter?.and) rootGqlFilter.and = []
      rootGqlFilter.and.push(makeGqlFilter(filter))
    // }
  })

  // If there is a search term, create a filter object for it and add it to the root filter object
  if (state.search) {
    if (!rootGqlFilter?.and) rootGqlFilter.and = []
    rootGqlFilter.and.push(makeSearchFilter(state.search))
  }

  return rootGqlFilter
}

// Object to map hex colours to their brightness values using the tinycolor2 library
let hexToIntMap = {}

// Function to get the brightness value of a hex colour
function hexToInt (hex) {
  // If the hex colour is not in the hexToIntMap, add it and its brightness value to the map
  if (!(hex in hexToIntMap)) {
    hexToIntMap[hex] = tinycolor(hex).getBrightness()
  }

  // Return the brightness value of the hex colour from the hexToIntMap
  return hexToIntMap[hex]
}

// Function to group colours by super group and order them by brightness
export const colourSuperGroups = (state) => {
  // List of desired order for super groups
  const desiredOrder = ['Yellow', 'Blue', 'Purple', 'Green', 'Red', 'Orange', 'Black', 'White']

  // Get the unique super groups from the state colours
  const superGroups = [...new Set(state.colours.map(v => v.superGroup))]

  // Create a return value array of objects with the hex, name, and groups of colours for each super group
  const returnVal = superGroups.map(superGroup => {
    // Get the colours for the super group
    const superGroupColours = state.colours.filter(v => v.superGroup === superGroup)

    // Get the unique groups from the super group colours
    const groups = [...new Set(superGroupColours.map(v => v.group))]

    // Create a groupValues array of objects with the hex, name, and colours for each group
    const groupValues = groups.map(group => {
      // Get the colours for the group
      const colours = state.colours.filter(v => v.group === group).sort((a, b) => hexToInt(a.hex) - hexToInt(b.hex))

      // Return an object with the hex, name, and colours for the group
      return {
        hex: colours[0].hex,
        name: group,
        colours
      }
    }).sort((a, b) => hexToInt(a.hex) - hexToInt(b.hex))

    // Return an object with the hex, name, and groups of colours for the super group
    return {
      hex: superGroupColours[0].hex,
      name: superGroup,
      groups: groupValues
    }
  }).sort((a, b) => {
    // Get the index of each super group in the desired order and use it to sort the super groups
    const aIndex = desiredOrder.indexOf(a.superGroup)
    const bIndex = desiredOrder.indexOf(b.superGroup)
    const aVal = aIndex < 0 ? 99999 : aIndex
    const bVal = bIndex < 0 ? 99999 : bIndex
    return aVal - bVal
  })

  // Return the return value array of objects with the hex, name, and groups of colours for each super group
  return returnVal
}

// Function to group colours by group and return an array of objects with the hex, name, and colours for each group
export const colourGroups = (state) => {
  // Get the unique groups from the state colours
  const groups = [...new Set(state.colours.map(v => v.group))]

  // Create a return value array of objects with the hex, name, and colours for each group
  return groups.map(group => {
    // Get the colours for the group
    let colours = state.colours.filter(v => v.group === group)

    // Return an object with the hex, name, and colours for the group
    return {
      hex: colours[0].hex,
      name: group,
      colours
    }
  })
}
